/**
 * React hook: WebSocket client for the on-device recycle-kiosk agent.
 *
 * Connects to the local agent (ws://127.0.0.1:8765 by default), parses every
 * inbound frame through the shared zod contract, and exposes connection
 * state, the latest health snapshot, the last parsed event, and a typed
 * command sender.
 *
 * Dormant by design: plain-signage screens run the exact same player build
 * with no recycle agent present. A refused/closed connection just keeps
 * retrying with exponential backoff (1s -> 30s) — no error UI, no crash.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  recycleEventSchema,
  RECYCLE_AGENT_WS_URL,
  RECYCLE_RECONNECT_MIN_MS,
  RECYCLE_RECONNECT_MAX_MS,
  type RecycleEvent,
  type RecycleCommand,
  type RecycleAgentStatus,
} from '@dooh/shared';

export interface UseRecycleAgentOptions {
  /** WS URL to connect to. Defaults to RECYCLE_AGENT_WS_URL. */
  url?: string;
  /** Called with every successfully-parsed inbound event. */
  onEvent?: (event: RecycleEvent) => void;
  /** false = never connect (dormant on plain-signage screens). Default true. */
  enabled?: boolean;
}

export interface UseRecycleAgentReturn {
  /** True while the WS connection is open. */
  agentOnline: boolean;
  /** Latest agent_status health snapshot, if any has been received. */
  health: RecycleAgentStatus | null;
  /** Most recently parsed inbound event. */
  lastEvent: RecycleEvent | null;
  /** Send a command envelope to the agent. No-op if the socket isn't open. */
  sendCommand: (command: RecycleCommand) => void;
}

function log(...args: unknown[]) {
  // Dev-only: silent in production kiosk builds to avoid unbounded console
  // growth on 24/7 devices (esp. the dormant-reconnect path on signage-only
  // screens that never reach an agent).
  if (import.meta.env.DEV) console.log('[RecycleAgent]', ...args);
}

export function useRecycleAgent(options: UseRecycleAgentOptions = {}): UseRecycleAgentReturn {
  const { url = RECYCLE_AGENT_WS_URL, onEvent, enabled = true } = options;

  const [agentOnline, setAgentOnline] = useState(false);
  const [health, setHealth] = useState<RecycleAgentStatus | null>(null);
  const [lastEvent, setLastEvent] = useState<RecycleEvent | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const backoffRef = useRef(RECYCLE_RECONNECT_MIN_MS);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closedByUnmountRef = useRef(false);
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;

  // connect/scheduleReconnect call each other, so both are declared with
  // `let` + assignment rather than mutually-referencing useCallbacks.
  const connectRef = useRef<() => void>(() => {});

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimerRef.current) return;
    const delay = backoffRef.current;
    reconnectTimerRef.current = setTimeout(() => {
      reconnectTimerRef.current = null;
      backoffRef.current = Math.min(backoffRef.current * 2, RECYCLE_RECONNECT_MAX_MS);
      connectRef.current();
    }, delay);
  }, []);

  const connect = useCallback(() => {
    if (!enabled) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (err) {
      log('WebSocket construction failed (dormant):', err);
      scheduleReconnect();
      return;
    }
    wsRef.current = ws;

    ws.onopen = () => {
      if (closedByUnmountRef.current) return;
      log('connected');
      backoffRef.current = RECYCLE_RECONNECT_MIN_MS;
      setAgentOnline(true);
    };
    ws.onclose = () => {
      if (closedByUnmountRef.current) return;
      setAgentOnline(false);
      wsRef.current = null;
      scheduleReconnect();
    };
    ws.onerror = () => {
      /* onclose fires next and handles reconnect scheduling */
    };
    ws.onmessage = (e: MessageEvent) => {
      if (closedByUnmountRef.current) return;
      let raw: unknown;
      try {
        raw = JSON.parse(typeof e.data === 'string' ? e.data : '');
      } catch {
        log('dropping non-JSON frame');
        return;
      }
      const parsed = recycleEventSchema.safeParse(raw);
      if (!parsed.success) {
        log('dropping invalid event', parsed.error.issues[0]?.message);
        return;
      }
      const event = parsed.data;
      if (event.type === 'agent_status') setHealth(prev => ({...prev, ...event.data}));
      setLastEvent(event);
      onEventRef.current?.(event);
    };
  }, [url, enabled, scheduleReconnect]);

  connectRef.current = connect;

  useEffect(() => {
    closedByUnmountRef.current = false;
    if (enabled) connect();
    return () => {
      closedByUnmountRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, enabled]);

  const sendCommand = useCallback((command: RecycleCommand) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) {
      log('sendCommand dropped — socket not open');
      return;
    }
    ws.send(JSON.stringify(command));
  }, []);

  return { agentOnline, health, lastEvent, sendCommand };
}
